import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { usePatientLabValues } from '../../../../../hooks/queries/usePatientLabValues';
import { formatDateForDisplay } from '../../../../../utils/formatters';

export interface RxRenalDosingChipProps {
  pharmacyId: string;
  patientId: string;
}

/**
 * PRX-145 (WP-20) / PRX-137 flags — "Renal dosing" in the Prescriptions toolbar
 * when the patient's latest CrCl is below the server's threshold. The flag is
 * the server's (`renalDosing`); this only shows it, with the CrCl and stage it
 * came from. Nothing renders when there is no flag (no labs, a CrCl that could
 * not be computed, or one at or above the threshold); the Kidney function card
 * on Patient info says which.
 */
export const RxRenalDosingChip: React.FC<RxRenalDosingChipProps> = ({ pharmacyId, patientId }) => {
  const { data } = usePatientLabValues(pharmacyId, patientId);
  const latest = data?.latest;
  if (!data?.renalDosing || !latest || latest.kidneyFunction.status !== 'computed') return null;

  const kf = latest.kidneyFunction;
  const detail =
    `CrCl ${kf.crclMlPerMin.toFixed(1)} mL/min (Cockcroft-Gault), CKD stage ${kf.ckdStageLabel}. ` +
    `Drawn ${formatDateForDisplay(latest.drawnOn)}.` +
    (latest.olderThan12Months ? ' Drawn more than 12 months ago.' : '');

  return (
    <Tooltip title={detail}>
      <Chip size="small" color="warning" label="Renal dosing" data-testid="rx-renal-dosing-chip" />
    </Tooltip>
  );
};

export default RxRenalDosingChip;
